import React, { createContext, FC, useState, useMemo, useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { TerritoryFilter, TerritoryFilterProps } from './filter';
import {
  postFilterToQueryKeyMap,
  promptFilterToQueryKeyMap,
  TerritoryActiveMainFilter,
  TerritoryLivePromptActiveFilter,
  TerritoryPostActiveFilter,
  TerritoryPromptActiveFilter,
} from '../constants';
import { useAnalytics } from '../../AnalyticsProvider/AnalyticsProvider';

export const TerritoryFilterContext = createContext({} as TerritoryFilter);

const TerritoryFilterProvider: FC<TerritoryFilterProps> = ({ children, initialTerritoryExploreFilter }) => {
  const router = useRouter();
  const query = router?.query;
  const territoryExplorePath = `/t/${query?.territorySlug}/explore/`;
  const defaultFilter = initialTerritoryExploreFilter || TerritoryActiveMainFilter.all;
  const { sendEvent } = useAnalytics();

  const [filters, setFilters] = useState({
    activeMainFilter: defaultFilter,
    activePostFilter: undefined as TerritoryPostActiveFilter | undefined,
    activePromptFilter: undefined as TerritoryPromptActiveFilter | undefined,
    activeLivePromptFilter: undefined as TerritoryLivePromptActiveFilter | undefined,
  });

  const resetFilters = () => {
    setFilters({
      activeMainFilter: TerritoryActiveMainFilter.all,
      activePostFilter: undefined,
      activePromptFilter: undefined,
      activeLivePromptFilter: undefined,
    });
    router?.push(territoryExplorePath);
  };

  const onMainFilterClick = (filter: TerritoryActiveMainFilter) => {
    sendEvent('territory_explore_page_main_filter_click', { filter_name: filter });

    setFilters((prevFilters) => ({
      ...prevFilters,
      activeMainFilter: prevFilters.activeMainFilter === filter ? TerritoryActiveMainFilter.all : filter,
    }));
    const newFilter = filters.activeMainFilter === filter ? TerritoryActiveMainFilter.all : filter;
    router?.replace(`${territoryExplorePath}${newFilter === TerritoryActiveMainFilter.all ? '' : newFilter}`);
  };

  const onPostFilterClick = (filter: TerritoryPostActiveFilter) => {
    sendEvent('territory_explore_page_post_filter_click', { filter_name: filter });

    setFilters((prevFilters) => ({
      ...prevFilters,
      activePostFilter: prevFilters.activePostFilter === filter ? undefined : filter,
    }));
    const queryKey = postFilterToQueryKeyMap[filter];
    const newFilter = filters.activePostFilter === filter ? undefined : filter;
    router?.replace({
      pathname: `${territoryExplorePath}${TerritoryActiveMainFilter.posts}`,
      query: newFilter ? { [queryKey]: newFilter } : {},
    });
  };

  const onPromptFilterClick = (filter: TerritoryPromptActiveFilter) => {
    sendEvent('territory_explore_page_prompt_filter_click', { filter_name: filter });

    setFilters((prevFilters) => ({
      ...prevFilters,
      activePromptFilter: prevFilters.activePromptFilter === filter ? undefined : filter,
    }));
    const queryKey = promptFilterToQueryKeyMap[filter];
    const newFilter = filters.activePromptFilter === filter ? undefined : filter;
    router?.replace({
      pathname: `${territoryExplorePath}${TerritoryActiveMainFilter.prompts}`,
      query: newFilter ? { [queryKey]: newFilter } : {},
    });
  };

  const onLivePromptFilterClick = (filter: TerritoryLivePromptActiveFilter) => {
    sendEvent('territory_explore_page_live_prompt_filter_click', { filter_name: filter });

    const newFilter = filters.activeLivePromptFilter === filter ? undefined : filter;
    setFilters((prevFilters) => ({ ...prevFilters, activeLivePromptFilter: newFilter }));
    const currentPromptStatus = filters.activePromptFilter || query?.['prompt-status'];
    router?.replace({
      pathname: `${territoryExplorePath}${TerritoryActiveMainFilter.prompts}`,
      query: {
        'prompt-status': currentPromptStatus,
        ...(newFilter ? { 'live-prompt-status': filter } : {}),
      },
    });
  };

  useEffect(() => {
    const extractFilter = <T,>(key1: string, key2?: string): T | undefined =>
      (query?.[key1] as T) || (key2 && (query?.[key2] as T)) || undefined;

    setFilters({
      activeMainFilter: defaultFilter,
      activePostFilter: extractFilter<TerritoryPostActiveFilter>('post-author', 'post-status'),
      activePromptFilter: extractFilter<TerritoryPromptActiveFilter>('prompt-author', 'prompt-status'),
      activeLivePromptFilter: extractFilter<TerritoryLivePromptActiveFilter>('live-prompt-status'),
    });
  }, [query, defaultFilter]);

  const providerValue = useMemo(
    () => ({
      filters,
      onMainFilterClick,
      onPostFilterClick,
      onPromptFilterClick,
      onLivePromptFilterClick,
      resetFilters,
    }),
    [filters],
  );

  return <TerritoryFilterContext.Provider value={providerValue}>{children}</TerritoryFilterContext.Provider>;
};

export default TerritoryFilterProvider;
